import { useMemo } from "react";
import { attachmentsListParams } from "@/features/attachments/lib/list-query-params";
import type {
	AttachmentsAdapter,
	AttachmentsDeleteVars,
	AttachmentsEditVars,
	AttachmentsUploadVars,
} from "@/features/attachments/types";
import {
	useDeleteRecordFile,
	useEditRecordFile,
	useUploadRecordFile,
} from "@/services/record/record.mutations";
import { recordInstanceQueryOptions } from "@/services/record/record.options";

/**
 * Pure list query options for record attachments (route loaders).
 */
export function createRecordAttachmentsListQueryOptions(args: {
	recordId: string;
	organizationId: string;
	start: number;
	count: number;
}) {
	return recordInstanceQueryOptions.files.list(
		args.recordId,
		args.start,
		args.count,
		attachmentsListParams(args.organizationId),
		{ organizationId: args.organizationId },
	);
}

/**
 * Record-scoped attachments adapter (hooks + query option factories).
 */
export function useRecordAttachmentsAdapter(
	recordId: string,
): AttachmentsAdapter {
	const uploadRecord = useUploadRecordFile();
	const deleteRecord = useDeleteRecordFile();
	const editRecord = useEditRecordFile();

	return useMemo(
		(): AttachmentsAdapter => ({
			kind: "record",
			entityId: recordId,
			listQueryOptions: ({ organizationId, start, count }) =>
				createRecordAttachmentsListQueryOptions({
					recordId,
					organizationId,
					start,
					count,
				}),
			contentQueryOptions: ({ organizationId, fileInfoId }) =>
				recordInstanceQueryOptions.files.content(recordId, fileInfoId, {
					organizationId,
				}),
			thumbnailQueryOptions: ({ organizationId, fileInfoId }) =>
				recordInstanceQueryOptions.files.thumbnail(recordId, fileInfoId, {
					organizationId,
				}),
			uploadMutation: {
				mutateAsync: (vars: AttachmentsUploadVars) =>
					uploadRecord.mutateAsync({
						recordId: vars.entityId,
						file: vars.file,
						organizationId: vars.organizationId,
						description: vars.description,
						entityAttachmentRequirementTagId:
							vars.entityAttachmentRequirementTagId,
						onUploadProgress: vars.onUploadProgress,
					}),
				reset: () => uploadRecord.reset(),
				isPending: uploadRecord.isPending,
				error: (uploadRecord.error as Error | null) ?? null,
			},
			deleteMutation: {
				mutateAsync: (vars: AttachmentsDeleteVars) =>
					deleteRecord.mutateAsync({
						recordId: vars.entityId,
						fileId: vars.fileId,
						organizationId: vars.organizationId,
					}),
				reset: () => deleteRecord.reset(),
				isPending: deleteRecord.isPending,
				error: (deleteRecord.error as Error | null) ?? null,
			},
			editMutation: {
				mutateAsync: (vars: AttachmentsEditVars) =>
					editRecord.mutateAsync({
						recordId: vars.entityId,
						fileId: vars.fileId,
						name: vars.name,
						description: vars.description,
						organizationId: vars.organizationId,
					}),
				reset: () => editRecord.reset(),
				isPending: editRecord.isPending,
				error: (editRecord.error as Error | null) ?? null,
			},
		}),
		[deleteRecord, editRecord, recordId, uploadRecord],
	);
}
